import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { forwardRef } from 'react';
import { OpportunityFloatingActionMenu } from '@client/components/OpportunityFloatingActionMenu';
import {
  OpportunityCtaButtonProps,
} from '@/pages/AdvisorDashboardOpportunities';
import { cn } from '@/lib/utils';
import { PriorityStatus } from '@/util/priorityStatus';

type OpportunityEngagementSheetProps = {
  trigger: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  clientsTable: React.ReactNode;
  floatingMenuCtaProps: OpportunityCtaButtonProps;
  selectedClientCount?: number;
};

export function OpportunityEngagementSheet({
  trigger,
  open,
  onOpenChange,
  title,
  description,
  clientsTable,
  floatingMenuCtaProps,
  selectedClientCount,
}: OpportunityEngagementSheetProps) {
  return (
    <Sheet
      modal={true}
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="bottom"
        className="h-[90%] overflow-y-auto rounded-2xl pb-10 md:h-auto md:max-h-[80%]"
      >
        <SheetHeader>
          <SheetTitle className="text-2xl font-semibold text-primary-dark">
            {title}
          </SheetTitle>
          <SheetDescription className="text-base text-gray-700">
            {description}
          </SheetDescription>
        </SheetHeader>
        {clientsTable}
        <OpportunityFloatingActionMenu
          selectedCount={selectedClientCount}
          floatingMenuCtaProps={floatingMenuCtaProps}
        />
      </SheetContent>
    </Sheet>
  );
}

export const EngagementSheetPrimaryBtn = forwardRef<
  HTMLButtonElement,
  {
    priorityStatus: PriorityStatus;
    onClick?: () => void;
    actionText?: string;
  }
>(({ priorityStatus, onClick, actionText }, ref) => {
  const noActionRequired =
    priorityStatus === 'No action required' ||
    priorityStatus === 'Not applicable';
  return (
    <Button
      ref={ref}
      onClick={onClick}
      className={cn(
        'rounded-full',
        !noActionRequired && 'bg-primary hover:bg-primary-hover',
      )}
      variant={noActionRequired ? 'outline' : 'default'}
      disabled={priorityStatus === 'Not applicable'}
    >
      {noActionRequired ? 'View Clients' : `${actionText ?? 'Review and send'}`}
    </Button>
  );
});
EngagementSheetPrimaryBtn.displayName = 'EngagementSheetPrimaryBtn';
